import {
    Flex,
    Text,
    Image,
    useDisclosure,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    useToast,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { AuthenticatedComponent } from '../../auth/keycloak';
import { BINGO_LICENSE, getHeaderImage, LEVIATHAN_LICENSE } from '../../service/games';
import {
    getProductByIDs,
    getUserInventory,
    InventoryDef,
    ProductDef,
    WrapperLicense,
} from '../../service/inventory';
import {
    bingoCurrencyProductIDs,
    CurrencyDisplay,
    getCurrencyForGame,
    getDisplayData,
    leviathanCurrencyProductIDs,
    purchaseProduct,
} from '../../util/storeUtil';
import { GameProduct } from './GameProduct';

interface GameProductListProps {
    license: string;
    streamGoldBalance: number;
    onProductsLoaded: () => void;
    onPurchase: (success: boolean) => void;
}

export const GameProductList: AuthenticatedComponent<GameProductListProps> = ({
    keycloak,
    license,
    streamGoldBalance,
    onProductsLoaded,
    onPurchase,
}) => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();

    const [activeProduct, setActiveProduct] = useState<ProductDef>();
    const [isPurchasing, setIsPurchasing] = useState<boolean>(false);
    const [productList, setProductList] = useState<ProductDef[]>([]);
    const [currency, setCurrency] = useState<InventoryDef>();
    const [displayData, setDisplayData] = useState<CurrencyDisplay>();

    const onPurchaseGameProduct = async (product: ProductDef) => {
        //first check for enough currency
        if (streamGoldBalance < product.cost[0].quantity) {
            toast({
                title: 'Insufficient streamgold',
                status: 'error',
                duration: 9000,
                isClosable: true,
            });
            onClose();
            return;
        }

        if (isPurchasing) {
            return;
        }

        setIsPurchasing(true);

        try {
            await purchaseProduct(product, keycloak.token, keycloak.subject);
            toast({
                title: 'Purchase Successful',
                status: 'success',
                duration: 9000,
                isClosable: true,
            });
            onPurchase(true);
        } catch (e) {
            toast({
                title: 'Purchase Error',
                description: (e as Error).message,
                status: 'error',
                duration: 9000,
                isClosable: true,
            });
            onPurchase(false);
        } finally {
            setIsPurchasing(false);
            onClose();
        }
    };

    useEffect(() => {
        const fetchItems = async () => {
            const products = await getProductByIDs(productIDs, WrapperLicense, keycloak.token);

            setProductList(products);
            onProductsLoaded();
        };

        let productIDs: string[] = [];

        switch (license) {
            case BINGO_LICENSE:
                productIDs = bingoCurrencyProductIDs;
                break;
            case LEVIATHAN_LICENSE:
                productIDs = leviathanCurrencyProductIDs;
                break;
        }

        if (productIDs.length > 0) {
            fetchItems();
        } else {
            onProductsLoaded();
        }
    }, [license, keycloak.token]);

    useEffect(() => {
        const fetchBalance = async () => {
            const itemId = getCurrencyForGame(license);
            if (itemId !== '') {
                const itemDefs = await getUserInventory(
                    keycloak.subject,
                    'currency',
                    itemId,
                    keycloak.token,
                );

                const display = getDisplayData(itemId);
                let qty = 0;

                if (itemDefs.length > 0) {
                    setCurrency(itemDefs[0]);
                    qty = itemDefs[0].quantity;
                }

                if (display) {
                    setDisplayData({
                        gameName: display.gameName,
                        displayName: display.displayName,
                        quantity: qty,
                        image: display.image || '',
                    });
                }
            }
        };

        fetchBalance();
    }, [license, isPurchasing, keycloak.token, keycloak.subject]);

    return (
        <Flex className={'ingame-product-section'}>
            <Flex className="ingame-balance">
                <Text>Balance:</Text>
                <Image src={displayData?.image} />
                <Text>
                    {displayData?.quantity} {displayData?.displayName}
                </Text>
            </Flex>
            <Flex className={'ingame-products'}>
                {productList.map((p) => (
                    <GameProduct
                        key={p.product_id}
                        product={p}
                        image={displayData?.image || getHeaderImage(license) || ''}
                        keycloak={keycloak}
                        isPurchasing={isPurchasing}
                        purchaseClick={() => {
                            setActiveProduct(p);
                            onOpen();
                        }}
                    />
                ))}
            </Flex>
            {activeProduct && (
                <Modal isOpen={isOpen} onClose={onClose} isCentered>
                    <ModalOverlay />
                    <ModalContent className={'purchase-modal'}>
                        <ModalHeader>Confirm Purchase</ModalHeader>
                        <ModalBody>
                            Do you want to Purchase {activeProduct.product_name} for{' '}
                            {activeProduct.cost[0].quantity} STREAMGOLD?
                        </ModalBody>
                        <ModalFooter className={'purchase-modal-footer'}>
                            <Text
                                as={'button'}
                                className={'cyan-background'}
                                onClick={() => {
                                    onPurchaseGameProduct(activeProduct);
                                }}
                            >
                                Confirm
                            </Text>
                            <Text as={'button'} className={'pink-background'} onClick={onClose}>
                                Cancel
                            </Text>
                        </ModalFooter>
                    </ModalContent>
                </Modal>
            )}
        </Flex>
    );
};
